/**
 * Who may delete an event-interest submission, and what the confirmation has to
 * name — in ONE copy, shared by the two surfaces that mount the control.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES**, exactly as
 * `event-interest-wording.ts` beside it does not. It answers *may this reader
 * be offered the control, and what is the dialog naming* — the request lives in
 * `DeleteSubmissionControl`, which is the only caller.
 *
 * ── WHY THE GATE IS A NAMED FUNCTION AND NOT `ability.isAdmin` AT TWO SITES ──
 *
 * Two mount points ask this, plus the tracker's dialog, which has to decide
 * whether to draw a footer at all — an empty footer is a bordered strip with
 * nothing in it. Three spellings of one rule is three chances to disagree about
 * who may destroy a record, and the divergence would be **silent**: the control
 * simply would not appear on one of the two screens, which this codebase has
 * already had reported as the feature being DELETED rather than hidden.
 *
 * ⚠️ **IT LIVES IN ITS OWN MODULE RATHER THAN BESIDE THE COMPONENT** because
 * `.oxlintrc.json` turns on `react/only-export-components`, and a file that
 * exports a component may not also export a plain function. That is a lint rule
 * rather than a design one, but the split it forces is the right shape anyway.
 */

import type { Ability } from '@/lib/permissions';

/**
 * A project this submission's `yes` answers named, as the confirmation renders
 * it.
 *
 * ⚠️ **A VIEW MODEL, so neither mount point hands the control a wire type** —
 * the rule the whole of `src/components/event-interest/` runs on. The tracker
 * builds these from its adapter's `SubmissionFestivalAnswer`, and the company
 * panel from the answer rows it already holds; both spell the underlying field
 * names in their own file and this module knows none of them.
 *
 * ⚠️ **A RETIRED PROJECT IS NAMED LIKE ANY OTHER.** The answer is a record of
 * what somebody said, so a festival retired since does not drop out of the
 * list — and a project silently missing from a confirmation is interest removed
 * without being named, which is the one thing this dialog exists to prevent.
 */
export interface RemovedProject {
  /** Stable render key. The project's id, as a string. */
  key: string;
  name: string;
}

/**
 * 🔴 **ADMINISTRATORS ONLY — owner's decision, and it matches the wire: the
 * route answers `403` below the administrator tier.**
 *
 * ⛔ **THE `ability` OBJECT, NEVER `user.role` AND NEVER `/permissions/me`.**
 * The permission list is advisory and wrong in both directions, and a role read
 * at a call site is the thing this app's whole gating model exists to stop.
 *
 * ⚠️ **THIS IS THE OPTIMISATION AND NOT THE GUARANTEE.** A role can be taken
 * away under a live access token — they last about five days — so the control
 * being hidden is not a reason for the caller to stop handling the `403`.
 * `DeleteSubmissionControl` handles it regardless.
 *
 * ⚠️ **It is deliberately NOT `canDelete('event-interest')`.** `event-interest`
 * is a real module gated on permission sets, and below the admin tier an absent
 * scope resolves **allowed** for `read` — so asking the permission resolver
 * here would be asking a question the server has not been put, about a route
 * that is guarded by the role instead. The module's own permissions migration
 * is not written yet, which is exactly the state in which that answer would
 * degrade OPEN on a destructive control.
 */
export function canDeleteSubmissions(ability: Ability): boolean {
  /*
   * 2026-10-01: deletes follow the set (owner's decision), and the backend gates
   * this route on `event-interest.delete`. *SUPERSEDED, the doc above:
   * administrators only, and "deliberately NOT `canDelete('event-interest')`".*
   */
  return ability.canDelete('event-interest');
}
